/**
 * Raw color palette — source of truth for hex values.
 * Mirrors the official Figma palette exactly (primitives + semantic tokens).
 * Component code should NOT import this directly — use semantic tokens from ./colors.ts.
 */
export const palette = {
  primitives: {
    base: {
      black: '#000000',
      white: '#FFFFFF'
    },
    blue: {
      blue0: '#E7F6FD80',
      blue50: '#E7F6FD',
      blue100: '#CFEDFB',
      blue200: '#9FDBF6',
      blue300: '#6EC9F2',
      blue400: '#3EB7ED',
      blue450: '#20ADE5',
      blue500: '#0EA5E9',
      blue600: '#0B84BA',
      blue700: '#08638C',
      blue800: '#06425D',
      blue900: '#03212F'
    },
    fucsia: {
      fucsia0: '#FDEDF580',
      fucsia50: '#FDEDF5',
      fucsia100: '#FBDAEB',
      fucsia200: '#F7B6D6',
      fucsia300: '#F491C2',
      fucsia400: '#F06DAD',
      fucsia500: '#EC4899',
      fucsia600: '#BF3A7C',
      fucsia700: '#922C5E',
      fucsia800: '#651D41',
      fucsia900: '#380F23'
    },
    green: {
      green0: '#E9F9EF80',
      green50: '#E9F9EF',
      green100: '#D3F3DF',
      green200: '#A7E8BF',
      green300: '#7ADC9E',
      green400: '#4ED17E',
      green500: '#22C55E',
      green600: '#1B9E4B',
      green700: '#147638',
      green800: '#0E4F26',
      green900: '#072713'
    },
    grey: {
      grey0: '#E9E9EE80',
      grey25: '#F3F3F6', // valor agregado manualmente para coincidir con Figma
      grey50: '#E9E9EE',
      grey100: '#D8DAE0',
      grey200: '#B8BAC6',
      grey300: '#979BAB',
      grey400: '#777B91',
      grey500: '#565C76',
      grey600: '#454A5E',
      grey700: '#343747',
      grey800: '#22252F',
      grey900: '#111218'
    },
    lime: {
      lime0: '#F2FAE780',
      lime50: '#F2FAE7',
      lime100: '#E6F6CE',
      lime200: '#CDEC9D',
      lime300: '#B3E36D',
      lime400: '#9AD93C',
      lime500: '#81D00B',
      lime600: '#67A609',
      lime700: '#4D7D07',
      lime800: '#345304',
      lime900: '#1A2A02'
    },
    orange: {
      orange0: '#FFF1E580',
      orange50: '#FFF1E5',
      orange100: '#FFC999',
      orange200: '#FFC999',
      orange300: '#FFAD66',
      orange400: '#FF9233',
      orange500: '#FF7700',
      orange600: '#CC5F00',
      orange700: '#994700',
      orange800: '#663000',
      orange900: '#663000'
    },
    purple: {
      purple0: '#F2E7FF80',
      purple25: '#F8F2FF',
      purple50: '#F2E7FF',
      purple75: '#D0B5FF',
      purple100: '#E4CEFF',
      purple200: '#CA9EFF',
      purple300: '#AF6DFF',
      purple400: '#953DFF',
      purple450: '#9747FF', // valor agregado manualmente para coincidir con Figma
      purple500: '#7A0CFF',
      purple550: '#7C3BED',
      purple575: '#793BED', // valor agregado manualmente para coincidir con Figma
      purple600: '#620ACC',
      purple700: '#490799',
      purple800: '#310566',
      purple900: '#180233'
    },
    slate: {
      slate500: '#64748B',
      slate900: '#0F172A'
    },
    teal: {
      teal500: '#24CCD2'
    },
    red: {
      red0: '#FDECEC80',
      red50: '#FDECEC',
      red100: '#FCDADA',
      red200: '#F9B4B4',
      red300: '#F58F8F',
      red400: '#F26969',
      red500: '#EF4444',
      red600: '#C13737',
      red700: '#932929',
      red800: '#661C1C',
      red900: '#380E0E'
    },
    yellow: {
      yellow0: '#FFF9E580',
      yellow50: '#FFF9E5',
      yellow100: '#FFF4CC',
      yellow200: '#FFE999',
      yellow300: '#FFDE66',
      yellow400: '#FFD333',
      yellow500: '#FFC800',
      yellow600: '#CCA000',
      yellow700: '#997800',
      yellow800: '#665000',
      yellow900: '#332800'
    }
  },

  semantic: {
    background: {
      'background-color-accent-default': '#F2E7FF80',
      'background-color-error-default': '#FDECEC',
      'background-color-info-default': '#E7F6FD80',
      'background-color-primary-default': '#FFFFFF',
      'background-color-secondary-default': '#E9E9EE80',
      'background-color-success-default': '#E9F9EF80',
      'background-color-warning-default': '#FFF1E580'
    },
    border: {
      'border-color-info-default': '#6EC9F2',
      'border-color-accent-default': '#CA9EFF',
      'border-color-error-default': '#F58F8F',
      'border-color-primary-default': '#565C76',
      'border-color-secondary-default': '#B8BAC6',
      'border-color-success-default': '#7ADC9E',
      'border-color-warning-default': '#FFAD66'
    },
    text: {
      default: {
        'text-color-accent-default': '#7A0CFF',
        'text-color-error-default': '#C13737',
        'text-color-link-default': '#0B84BA',
        'text-color-primary-default': '#22252F',
        'text-color-secondary-default': '#565C76',
        'text-color-success-default': '#1B9E4B',
        'text-color-warning-default': '#CC5F00'
      },
      disabled: {
        'text-color-accent-disabled': '#AF6DFF',
        'text-color-error-disabled': '#F58F8F',
        'text-color-link-disabled': '#6EC9F2',
        'text-color-primary-disabled': '#777B91',
        'text-color-secondary-disabled': '#979BAB',
        'text-color-success-disabled': '#7ADC9E',
        'text-color-warning-disabled': '#FFAD66'
      }
    },
    icon: {
      default: {
        'icon-color-accent-default': '#7A0CFF',
        'icon-color-error-default': '#C13737',
        'icon-color-link-default': '#0B84BA',
        'icon-color-primary-default': '#22252F',
        'icon-color-secondary-default': '#565C76',
        'icon-color-success-default': '#1B9E4B',
        'icon-color-warning-default': '#CC5F00'
      },
      disabled: {
        'icon-color-link-disabled': '#6EC9F2',
        'icon-color-accent-disabled': '#AF6DFF',
        'icon-color-error-disabled': '#F58F8F',
        'icon-color-primary-disabled': '#777B91',
        'icon-color-secondary-disabled': '#565C76',
        'icon-color-success-disabled': '#7ADC9E',
        'icon-color-warning-disabled': '#FFAD66'
      }
    }
  }
} as const;
